import React from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface ToastNotificationProps {
  open: boolean;
  message: string;
  severity?: AlertColor;
  autoHideDuration?: number;
  onClose: () => void;
  anchorOrigin?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

/**
 * Reusable Toast Notification Component
 * 
 * A standardized toast notification using MUI Snackbar and Alert.
 * Used across the application for consistent user feedback.
 * 
 * @example
 * ```tsx
 * <ToastNotification
 *   open={toastOpen}
 *   message="Operation successful"
 *   severity="success"
 *   onClose={() => setToastOpen(false)}
 * />
 * ```
 */
export const ToastNotification: React.FC<ToastNotificationProps> = ({
  open,
  message,
  severity = 'success',
  autoHideDuration = 2000,
  onClose,
  anchorOrigin = { vertical: 'top', horizontal: 'center' },
}) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
    >
      <Alert
        onClose={onClose}
        severity={severity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default ToastNotification;
